import React, { useState, useEffect } from "react";
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
  useParams,
} from "react-router-dom";
import { apiService } from "../services/api";
import {
  DataRequest,
  User,
  UserWithProjects,
  Project,
  ProjectOverview,
} from "../types/api";
import { UserTable } from "./UserTable";
import { DatasetBrowser } from "./DatasetBrowser";
import { DataRequestForm } from "./DataRequestForm";
import { CreateProjectDialog } from "./CreateProjectDialog";
import { InviteUserWidget } from "./InviteUserWidget";
import { AdminRequestsSplitView } from "./AdminRequestsSplitView";
import { UnifiedConsole } from "./UnifiedConsole";

interface AdminDashboardProps {
  user: User | null;
}

export const AdminDashboard: React.FC<AdminDashboardProps> = ({ user }) => {
  const isOrgAdmin = user?.role === "manager";

  return (
    <div className="admin-dashboard">
      <style>{`
        .admin-dashboard {
          max-width: 1400px;
          margin: 0 auto;
        }

        .admin-section {
          background: white;
          border-radius: 8px;
          padding: 24px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
          margin-bottom: 24px;
        }

        .stat-card {
          background: var(--surface-1);
          border-radius: var(--radius);
          padding: 1rem;
          text-align: left;
          cursor: pointer;
          border: 0.5px solid transparent;
          transition: border-color 0.15s ease;
        }

        .stat-card:hover {
          border-color: var(--border-strong);
        }

        .stat-label {
          font-size: 13px;
          color: var(--text-secondary);
          margin: 0 0 4px;
        }

        .stat-number {
          font-size: 24px;
          font-weight: 500;
          margin: 0;
          color: var(--text-primary);
        }

        .stat-number.warning {
          color: var(--text-warning);
        }

        .attention-section {
          margin-bottom: 28px;
        }

        .section-label {
          font-size: 13px;
          font-weight: 500;
          color: var(--text-secondary);
          margin: 0 0 10px;
        }

        .attention-list {
          border: 0.5px solid var(--border);
          border-radius: var(--radius);
          overflow: hidden;
        }

        .attention-row {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 11px 14px;
          border-bottom: 0.5px solid var(--border);
        }

        .attention-row:last-child {
          border-bottom: none;
        }

        .attention-icon {
          color: var(--text-warning);
          flex-shrink: 0;
        }

        .attention-text {
          flex: 1;
          min-width: 0;
        }

        .attention-title {
          font-size: 14px;
          font-weight: 500;
          margin: 0;
          color: var(--text-primary);
        }

        .attention-sub {
          font-size: 12px;
          color: var(--text-muted);
          margin: 2px 0 0;
        }

        .attention-review-btn {
          padding: 6px 12px;
          font-size: 13px;
          font-family: inherit;
          border-radius: var(--radius);
          border: 0.5px solid var(--border-strong);
          background: var(--surface-2);
          cursor: pointer;
          flex-shrink: 0;
        }

        .attention-review-btn:hover {
          background: var(--surface-1);
        }

        .your-projects-section {
          margin-bottom: 24px;
        }

        .your-projects-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 10px;
        }

        .view-all-btn {
          padding: 6px 12px;
          font-size: 13px;
          font-family: inherit;
          border-radius: var(--radius);
          border: 0.5px solid var(--border-strong);
          background: var(--surface-2);
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 5px;
        }

        .view-all-btn:hover {
          background: var(--surface-1);
        }

        .project-row {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 12px 14px;
          border-bottom: 0.5px solid var(--border);
          cursor: pointer;
        }

        .project-row:last-child {
          border-bottom: none;
        }

        .project-row:hover {
          background: var(--surface-1);
        }

        .project-status-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          flex-shrink: 0;
        }

        .invite-pending-note {
          font-size: 12px;
          color: var(--text-muted);
          display: flex;
          align-items: center;
          gap: 4px;
          flex-shrink: 0;
        }

        .open-requests-pill {
          font-size: 12px;
          background: var(--bg-warning);
          color: var(--text-warning);
          padding: 3px 8px;
          border-radius: var(--radius);
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 4px;
          flex-shrink: 0;
        }

        .no-open-requests-note {
          font-size: 12px;
          color: var(--text-muted);
          flex-shrink: 0;
        }

        .project-list-header-actions {
          display: flex;
          align-items: center;
          gap: 14px;
        }

        .project-archive-toggle {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          color: var(--text-secondary);
          cursor: pointer;
          white-space: nowrap;
        }

        .project-list-row.archived {
          background: var(--surface-1);
        }

        .project-list-row.archived .project-list-name,
        .project-list-row.archived .project-list-desc {
          color: var(--text-muted);
        }

        .project-list-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }

        .project-list-title {
          margin: 0 0 2px;
        }

        .project-list-count {
          font-size: 13px;
          color: var(--text-muted);
          margin: 0;
        }

        .new-project-btn {
          background: var(--fill-primary);
          color: var(--on-primary);
          border: none;
          padding: 8px 14px;
          border-radius: var(--radius);
          font-size: 14px;
          font-family: inherit;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 6px;
          cursor: pointer;
        }

        .project-list {
          border: 0.5px solid var(--border);
          border-radius: var(--radius);
          overflow: hidden;
        }

        .project-list-row {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 12px 14px;
          border-bottom: 0.5px solid var(--border);
          border-left: 2px solid transparent;
          cursor: pointer;
        }

        .project-list-row:last-child {
          border-bottom: none;
        }

        .project-list-row:hover {
          background: var(--bg-accent-muted);
          border-left-color: var(--border-accent);
        }

        .project-list-row:hover .project-list-name {
          color: var(--text-accent);
        }

        .project-list-info {
          flex: 1;
          min-width: 0;
        }

        .project-list-titlerow {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .project-list-name {
          font-size: 14px;
          font-weight: 500;
          margin: 0;
        }

        .project-list-meta {
          font-size: 11px;
          color: var(--text-muted);
          flex-shrink: 0;
        }

        .project-list-desc {
          font-size: 12px;
          color: var(--text-muted);
          margin: 2px 0 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .project-list-stats {
          display: flex;
          gap: 16px;
          flex-shrink: 0;
        }

        .project-list-stat {
          font-size: 12px;
          color: var(--text-secondary);
          display: flex;
          align-items: center;
          gap: 4px;
        }

      `}</style>

      {/* No "Admin/PI Dashboard" title, and no tab nav here: the persistent
          shell (PublicLayout) now renders that strip above every page, so the
          viewer always knows where they are without it repeating per-tab. */}
      <Routes>
        <Route
          index
          element={
            <UnifiedConsole
              currentUserId={user?.id}
              currentUserName={user?.name}
              currentUserRole={user?.role}
              projectsRoute="/admin/projects"
            />
          }
        />
        <Route
          path="users"
          element={<UsersView isOrgAdmin={isOrgAdmin} currentUser={user} />}
        />
        <Route
          path="projects"
          element={
            <ProjectsView isOrgAdmin={isOrgAdmin} currentUserId={user?.id} />
          }
        />
        <Route path="projects/:id" element={<AdminProjectDetailRedirect />} />
        <Route
          path="projects/:id/new-request"
          element={<AdminProjectNewRequestRoute user={user} />}
        />
        <Route path="requests" element={<RequestsView user={user} />} />
        <Route
          path="requests/:requestId"
          element={<RequestsView user={user} />}
        />
        {/* Old path, kept for bookmarks and any stale notification links */}
        <Route
          path="data-requests"
          element={<Navigate to="/admin/requests" replace />}
        />
        <Route
          path="data-requests/:requestId"
          element={<OldAdminRequestDetailRedirect />}
        />
        <Route path="datasets" element={<AdminDatasetsView />} />
        <Route
          path="datasets/new-request"
          element={<AdminNewDataRequestRoute user={user} />}
        />
        <Route
          path="files"
          element={<Navigate to="/admin/datasets" replace />}
        />
      </Routes>
    </div>
  );
};

// Redirect /admin/projects/:id → /projects/:id (unified project view)
const AdminProjectDetailRedirect: React.FC = () => {
  const { id } = useParams();
  return <Navigate to={`/projects/${id}`} replace />;
};

// Redirect old /admin/data-requests/:id links into the unified requests view
const OldAdminRequestDetailRedirect: React.FC = () => {
  const { requestId } = useParams();
  return <Navigate to={`/admin/requests/${requestId}`} replace />;
};

// Admin Project New Request Route - create data request with project pre-selected
const AdminProjectNewRequestRoute: React.FC<{ user: User | null }> = ({
  user,
}) => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const projectId = parseInt(id || "0", 10);
  const projectTitle = location.state?.projectTitle || "Project";
  const preselectedDataset = location.state?.preselectedDataset || null;

  if (!projectId) {
    return <div className="no-data">Invalid project ID</div>;
  }

  return (
    <div className="admin-section">
      <DataRequestForm
        preselectedProjectId={projectId}
        preselectedDataset={preselectedDataset}
        user={user}
        onRequestCreated={() => navigate(`/admin/projects/${projectId}`)}
      />
    </div>
  );
};

// Admin Datasets View - wraps DatasetBrowser for admin context
const AdminDatasetsView: React.FC = () => (
  <div className="admin-section">
    <DatasetBrowser navigateToRequestRoute="/admin/datasets/new-request" />
  </div>
);

// Admin New Data Request Route - wraps DataRequestForm for admin context
const AdminNewDataRequestRoute: React.FC<{ user: User | null }> = ({
  user,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const preselectedDataset = location.state?.preselectedDataset || null;

  return (
    <div className="admin-section">
      <DataRequestForm
        preselectedDataset={preselectedDataset}
        user={user}
        onRequestCreated={() => navigate("/admin/requests")}
      />
    </div>
  );
};

// Users View Component
interface UsersViewProps {
  isOrgAdmin: boolean;
  currentUser: User | null;
}

const UsersView: React.FC<UsersViewProps> = ({ isOrgAdmin, currentUser }) => {
  const [users, setUsers] = useState<UserWithProjects[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [managedUserIds, setManagedUserIds] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [userData, projectsData] = await Promise.all([
        apiService.adminGetUsersWithProjects(),
        apiService.adminGetProjects(),
      ]);
      setUsers(userData || []);
      setProjects(projectsData || []);

      if (isOrgAdmin) {
        // Org manager: manages everyone in the org.
        setManagedUserIds(new Set(userData?.map((u) => u.id) || []));
      } else {
        // PI: manages only users who share a project they manage (empty => none).
        // adminGetProjects() already returns just the caller's managed projects.
        const piProjectIds = new Set((projectsData || []).map((p) => p.id));
        const userIds = new Set<number>();
        (userData || []).forEach((u) => {
          if (u.projects?.some((p) => piProjectIds.has(p.project_id))) {
            userIds.add(u.id);
          }
        });
        setManagedUserIds(userIds);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load data");
      setUsers([]);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleDisable2FA = async (userId: number) => {
    try {
      setActionLoading(true);
      setError(null);
      setSuccess(null);
      await apiService.adminDisableTwoFA(userId);
      setSuccess(
        "2FA disabled successfully. User will be required to set up 2FA on next login.",
      );
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to disable 2FA");
    } finally {
      setActionLoading(false);
    }
  };

  const handlePromotePI = async (userId: number) => {
    try {
      setActionLoading(true);
      setError(null);
      setSuccess(null);
      await apiService.adminPromotePI(userId);
      setSuccess("User promoted to PI.");
      await loadData();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to promote user to PI",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevokePI = async (userId: number) => {
    try {
      setActionLoading(true);
      setError(null);
      setSuccess(null);
      await apiService.adminRevokePI(userId);
      setSuccess(
        "PI status revoked. User demoted to Researcher and removed as PI from their active projects.",
      );
      await loadData();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to revoke PI status",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddToProject = async (userId: number, projectId: number) => {
    try {
      setActionLoading(true);
      setError(null);
      setSuccess(null);
      await apiService.adminAssignUserToProject(
        projectId,
        userId,
        "researcher",
      );
      setSuccess("User added to project successfully.");
      await loadData();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to add user to project",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveFromProject = async (userId: number, projectId: number) => {
    try {
      setActionLoading(true);
      setError(null);
      setSuccess(null);
      await apiService.adminRemoveUserFromProject(projectId, userId);
      setSuccess("User removed from project successfully.");
      await loadData();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to remove user from project",
      );
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading users...</div>;
  }

  if (error && users.length === 0) {
    return (
      <div className="admin-section">
        <div className="error-message">{error}</div>
        <button onClick={loadData}>Retry</button>
      </div>
    );
  }

  return (
    <div className="admin-section">
      <style>{`
        .actions-menu {
          position: relative;
        }
        .actions-button {
          background: none;
          border: none;
          font-size: 20px;
          cursor: pointer;
          padding: 4px 8px;
          border-radius: 4px;
          color: #495057;
        }
        .actions-button:hover {
          background: #f8f9fa;
        }
        .actions-dropdown {
          position: absolute;
          right: 0;
          top: 100%;
          background: white;
          border: 1px solid #dee2e6;
          border-radius: 4px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.15);
          z-index: 1000;
          min-width: 200px;
          margin-top: 4px;
        }
        .actions-dropdown button {
          display: block;
          width: 100%;
          padding: 10px 16px;
          text-align: left;
          border: none;
          background: none;
          cursor: pointer;
          font-size: 14px;
          color: #212529;
          border-bottom: 1px solid #f8f9fa;
        }
        .actions-dropdown button:hover {
          background: #f8f9fa;
        }
        .actions-dropdown button:last-child {
          border-bottom: none;
        }
        .actions-dropdown button.danger {
          color: #dc3545;
        }
        .actions-dropdown button.danger:hover {
          background: #fff5f5;
        }
        .submenu {
          padding-left: 16px;
          background: #f8f9fa;
        }
        .submenu button {
          padding: 8px 16px;
          font-size: 13px;
        }
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 2000;
        }
        .modal-content {
          background: white;
          padding: 24px;
          border-radius: 8px;
          max-width: 500px;
          width: 90%;
          box-shadow: 0 4px 16px rgba(0,0,0,0.2);
        }
        .modal-buttons {
          display: flex;
          gap: 12px;
          margin-top: 20px;
          justify-content: flex-end;
        }
      `}</style>

      <h2>User Management</h2>

      {error && (
        <div
          style={{
            marginBottom: "16px",
            padding: "12px",
            background: "#f8d7da",
            border: "1px solid #f5c6cb",
            borderRadius: "4px",
            color: "#721c24",
          }}
        >
          {error}
        </div>
      )}

      {success && (
        <div
          style={{
            marginBottom: "16px",
            padding: "12px",
            background: "#d4edda",
            border: "1px solid #c3e6cb",
            borderRadius: "4px",
            color: "#155724",
          }}
        >
          {success}
        </div>
      )}

      {currentUser && projects.length > 0 && (
        <InviteUserWidget
          projects={projects}
          inviterName={currentUser.name}
          inviterEmail={currentUser.email}
          onInvited={loadData}
        />
      )}

      <p>Total Users: {users.length}</p>

      {users.length === 0 ? (
        <div className="no-data">No users found</div>
      ) : (
        <>
          {(() => {
            const managedUsers = users.filter((u) => managedUserIds.has(u.id));
            const otherUsers = users.filter((u) => !managedUserIds.has(u.id));

            const renderUserSection = (
              userList: User[],
              title: string,
              showNote: boolean = false,
              isOtherUsers: boolean = false,
            ) => {
              if (userList.length === 0) return null;

              return (
                <div style={{ marginBottom: "32px" }}>
                  <h3
                    style={{
                      marginBottom: "12px",
                      fontSize: "18px",
                      fontWeight: "600",
                      color: "#495057",
                    }}
                  >
                    {title} ({userList.length})
                  </h3>
                  <UserTable
                    users={userList}
                    onRemoveFromProject={handleRemoveFromProject}
                    onAddToProject={handleAddToProject}
                    onDisable2FA={handleDisable2FA}
                    onPromotePI={handlePromotePI}
                    onRevokePI={handleRevokePI}
                    viewerIsManager={isOrgAdmin}
                    viewerProjects={projects}
                    actionLoading={actionLoading}
                    showActions={true}
                  />
                </div>
              );
            };

            return (
              <>
                {renderUserSection(
                  managedUsers,
                  isOrgAdmin
                    ? "All Organization Users"
                    : "Users in Your Projects",
                  false,
                  false,
                )}
                {renderUserSection(
                  otherUsers,
                  "Other Organization Users",
                  !isOrgAdmin,
                  true,
                )}
              </>
            );
          })()}
        </>
      )}
    </div>
  );
};

// Projects View Component
interface ProjectsViewProps {
  isOrgAdmin: boolean;
  currentUserId?: number;
}

const ProjectsView: React.FC<ProjectsViewProps> = ({
  isOrgAdmin,
  currentUserId,
}) => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<ProjectOverview[]>([]);
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  useEffect(() => {
    loadProjects(showArchived);
  }, [showArchived]);

  const loadProjects = async (includeArchived: boolean) => {
    try {
      setLoading(true);
      setError(null);
      const projectData =
        await apiService.adminGetProjectsOverview(includeArchived);
      setProjects(projectData || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load projects");
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (loading) {
    return <div className="loading">Loading projects...</div>;
  }

  if (error) {
    return (
      <div className="admin-section">
        <div className="error-message">{error}</div>
        <button onClick={() => loadProjects(showArchived)}>Retry</button>
      </div>
    );
  }

  return (
    <div className="admin-section">
      <div className="project-list-header">
        <div>
          <h2 className="project-list-title">Project management</h2>
          <p className="project-list-count">
            {projects.length} project{projects.length === 1 ? "" : "s"}
            {showArchived &&
              `, ${projects.filter((p) => p.is_archived).length} archived`}
          </p>
        </div>
        <div className="project-list-header-actions">
          <label className="project-archive-toggle">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
            />
            Show archived
          </label>
        <button
          className="new-project-btn"
          onClick={() => setShowCreateDialog(true)}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M12 5v14M5 12h14" />
          </svg>
          New project
        </button>
        </div>
      </div>

      {projects.length === 0 ? (
        <div className="no-data">
          {showArchived
            ? "No projects yet"
            : "No active projects — tick “Show archived” if you are looking for a retired one"}
        </div>
      ) : (
        <div className="project-list">
          {projects.map((project) => (
            <div
              key={project.id}
              className={`project-list-row${project.is_archived ? " archived" : ""}`}
              onClick={() => navigate(`/projects/${project.id}`)}
            >
              <span
                className="project-status-dot"
                style={{
                  background: project.is_active
                    ? "var(--text-success)"
                    : "var(--text-muted)",
                }}
              />
              <div className="project-list-info">
                <div className="project-list-titlerow">
                  <p className="project-list-name">
                    {project.title}
                    {project.created_by_user_id != null &&
                      project.created_by_user_id === currentUserId && (
                        <span
                          className="badge owner"
                          style={{ marginLeft: 6 }}
                        >
                          Owner
                        </span>
                      )}
                  </p>
                  <span className="project-list-meta">
                    ID {project.id} · {formatDate(project.created_at)}
                  </span>
                </div>
                {project.description && (
                  <p className="project-list-desc">{project.description}</p>
                )}
              </div>
              <div className="project-list-stats">
                <span className="project-list-stat">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="var(--text-muted)"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="8" r="4" />
                    <path d="M4 21v-2a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v2" />
                  </svg>
                  {project.user_count}
                </span>
                <span className="project-list-stat">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="var(--text-muted)"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M22 12h-6l-2 3h-4l-2-3H2" />
                    <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
                  </svg>
                  {project.open_requests}
                </span>
                <span className="project-list-stat">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="var(--text-muted)"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <ellipse cx="12" cy="5" rx="9" ry="3" />
                    <path d="M3 5v14a9 3 0 0 0 18 0V5" />
                    <path d="M3 12a9 3 0 0 0 18 0" />
                  </svg>
                  {project.files_count}
                </span>
              </div>
              <span
                className={`badge ${
                  project.is_archived
                    ? "archived"
                    : project.is_active
                      ? "active"
                      : "inactive"
                }`}
              >
                {project.is_archived
                  ? "Archived"
                  : project.is_active
                    ? "Active"
                    : "Inactive"}
              </span>
            </div>
          ))}
        </div>
      )}

      {showCreateDialog && (
        <CreateProjectDialog
          onClose={() => setShowCreateDialog(false)}
          onSuccess={() => {
            setShowCreateDialog(false);
            loadProjects(showArchived);
          }}
        />
      )}
    </div>
  );
};

// Requests View Component — unified split-view serving both access and data
// requests, aggregated across every project the viewer manages (PI) or the
// whole org (manager). Optionally deep-links to one request via :requestId.
const RequestsView: React.FC<{ user: User | null }> = ({ user }) => {
  const navigate = useNavigate();
  const { requestId } = useParams();
  const [requests, setRequests] = useState<DataRequest[]>([]);
  const [datasetNames, setDatasetNames] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    try {
      setLoading(true);
      setError(null);
      const [requestsResponse, datasetsResponse] = await Promise.all([
        apiService.getAdminDataRequests(),
        apiService.getDatasets().catch(() => ({ datasets: [], total: 0 })),
      ]);
      setRequests(requestsResponse?.requests || []);

      const names: Record<number, string> = {};
      (datasetsResponse.datasets || []).forEach((ds) => {
        names[ds.id] = ds.display_name;
      });
      setDatasetNames(names);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load data requests",
      );
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (
    id: number,
    status: "approved" | "rejected",
    notes: string,
  ) => {
    await apiService.reviewDataRequest(id, { status, admin_notes: notes });
    await loadRequests();
  };

  if (loading) {
    return <div className="loading">Loading data requests...</div>;
  }

  if (error) {
    return (
      <div className="admin-section">
        <div className="error-message">{error}</div>
        <button onClick={loadRequests}>Retry</button>
      </div>
    );
  }

  return (
    <div className="admin-section">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
        }}
      >
        <h2 style={{ margin: 0 }}>Data Requests</h2>
        <button
          onClick={() => navigate("/admin/datasets/new-request")}
          style={{
            background: "var(--fill-primary)",
            color: "var(--on-primary)",
            border: "none",
            padding: "8px 14px",
            borderRadius: "var(--radius)",
            fontSize: "14px",
            fontWeight: 500,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "6px",
          }}
        >
          + New Request
        </button>
      </div>

      {requests.length === 0 ? (
        <div className="no-data">No data requests found</div>
      ) : (
        <AdminRequestsSplitView
          requests={requests}
          datasetNames={datasetNames}
          currentUserId={user?.id}
          currentUserRole={user?.role}
          onReview={handleReview}
          onDataUploaded={loadRequests}
          showProjectFilter={true}
          selectedId={requestId ? parseInt(requestId, 10) : undefined}
          onSelect={(id) =>
            navigate(`/admin/requests/${id}`, { replace: true })
          }
        />
      )}
    </div>
  );
};
